import { message } from "antd";
import S3 from "aws-sdk/clients/s3";
import * as bb from "bluebird";
import strings from "i18n/strings";
import generate from "nanoid/generate";
import api from "./api";

const CREDENTIALS_REFRESH_BUFFER_MS = 5 * 60 * 1000;

function isAccessError(error) {
  // Axios errors carry the API response here. An S3 403 (e.g. ExpiredToken)
  // must still be allowed to refresh its credentials and retry.
  return (
    error &&
    error.response &&
    (error.response.status === 401 || error.response.status === 403)
  );
}

function isExpiredTokenError(error) {
  return (
    error &&
    (error.code === "ExpiredToken" ||
      error.name === "ExpiredToken" ||
      /token has expired/i.test(error.message || ""))
  );
}

export default async (
  authorId,
  matterId,
  fileList,
  onProgress,
  onFatalError
) => {
  window.onbeforeunload = function () {
    return strings.interruptUpload;
  };
  let stopped = false;
  let uploadCount = 0;
  let uploadedFiles = [];
  let uploadInstance;
  let credentialsUpdateInterval;
  const activeTransfers = new Set();
  const retryWaits = new Set();

  function cleanup() {
    window.onbeforeunload = null;
    clearInterval(credentialsUpdateInterval);
    retryWaits.forEach((cancel) => cancel());
  }

  function stop(error) {
    if (stopped) return;
    stopped = true;
    cleanup();
    activeTransfers.forEach((transfer) => transfer.abort());
    onFatalError(error);
  }

  function waitBeforeRetry() {
    return new Promise((resolve) => {
      let timer;
      const resume = () => {
        retryWaits.delete(cancel);
        resolve();
      };
      const cancel = () => {
        clearTimeout(timer);
        resume();
      };
      retryWaits.add(cancel);
      timer = setTimeout(resume, 10000);
    });
  }

  let s3;
  let credentialsExpiration = 0;
  let credentialsRefresh = null;
  async function refreshS3Credentials() {
    if (stopped) return;
    if (credentialsRefresh) return credentialsRefresh;

    credentialsRefresh = api
      .getS3Credentials(uploadInstance.id)
      .then((s3Credentials) => {
        if (stopped) return;
        credentialsExpiration = s3Credentials.Expiration
          ? new Date(s3Credentials.Expiration).getTime()
          : 0;

        if (Number.isNaN(credentialsExpiration)) credentialsExpiration = 0;

        s3 = new S3({
          region: "eu-west-1",
          accessKeyId: s3Credentials.AccessKeyId,
          secretAccessKey: s3Credentials.SecretAccessKey,
          sessionToken: s3Credentials.SessionToken,
        });
      });

    try {
      await credentialsRefresh;
    } finally {
      credentialsRefresh = null;
    }
  }

  async function ensureFreshS3Credentials() {
    if (
      !s3 ||
      (credentialsExpiration &&
        Date.now() + CREDENTIALS_REFRESH_BUFFER_MS >= credentialsExpiration)
    ) {
      await refreshS3Credentials();
    }
  }

  async function uploadFile(file) {
    while (!stopped) {
      let transfer;
      try {
        await ensureFreshS3Credentials();
        if (stopped) return;
        transfer = s3.upload({
          Bucket: process.env.REACT_APP_S3_BUCKET,
          Key: `batch-upload/${authorId}/${uploadInstance.id}/${generate(
            "1234567890abcdef",
            15
          )}.jpg`,
          Body: file,
        });
        activeTransfers.add(transfer);
        try {
          await transfer.promise();
        } finally {
          activeTransfers.delete(transfer);
        }
      } catch (error) {
        if (stopped) return;
        if (isAccessError(error)) {
          stop(error);
          return;
        }
        if (isExpiredTokenError(error)) {
          try {
            await refreshS3Credentials();
          } catch (refreshError) {
            if (isAccessError(refreshError)) {
              stop(refreshError);
              return;
            }
            console.warn(
              "Failed to refresh expired S3 upload credentials",
              refreshError
            );
          }
        }
        if (stopped) return;
        message.warn(
          `${strings.errorWhileUploadingFile} ${file.name}: ${error.message}. ${strings.restartIn}`
        );
        await waitBeforeRetry();
        continue;
      }
      if (stopped) return;
      uploadCount++;
      uploadedFiles = uploadedFiles.concat([file]);
      onProgress(
        Math.ceil((uploadCount / fileList.length) * 100),
        uploadedFiles
      );
      return;
    }
  }

  try {
    uploadInstance = await api.createUpload(matterId, fileList.length);
    await refreshS3Credentials();
    credentialsUpdateInterval = setInterval(() => {
      refreshS3Credentials().catch((error) => {
        if (isAccessError(error)) {
          stop(error);
          return;
        }
        console.warn("Failed to refresh S3 upload credentials", error);
      });
    }, 1000 * 60 * 55); // 5 minutes before expiration
    await bb.resolve(fileList).map(uploadFile, { concurrency: 10 });
    if (stopped) return false;
    await api.finishUpload(uploadInstance.id);
    return !stopped;
  } catch (error) {
    stop(error);
    return false;
  } finally {
    cleanup();
  }
};
